import { categSelects, $side_panel } from './_context.js';

/** Between multiple slugs for one filter; avoids comma (%2C) in the address bar. Unreserved in URLs and not used by WP slug rules. */
const CATEG_SLUG_SEP = '~';

function getAdditionnalCategAttribute() {
    if (typeof document === 'undefined' || !document.body) {
        return '';
    }
    return document.body.getAttribute('data-additionnal-categ') || '';
}

function normalizeMultiSelectSnapshot(val) {
    if (val == null || val === '') {
        return [];
    }
    const arr = Array.isArray(val) ? val.slice() : [String(val)];
    return arr.map(String).sort();
}

function collectActiveCategorySlugsFromUi() {
    const slugs = new Set();
    if (categSelects && categSelects.length) {
        Array.prototype.forEach.call(categSelects, function (el) {
            normalizeMultiSelectSnapshot(el.value).forEach(function (s) {
                slugs.add(s);
            });
        });
    }
    const extra = getAdditionnalCategAttribute().trim();
    if (extra) {
        slugs.add(extra);
    }
    return slugs;
}

/**
 * Mirror user interactions: expand the results panel and category accordions when filters are active
 * (e.g. after hydrating `?categorie=` from the URL).
 */
export function expandSidePanelUiForActiveFilters() {
    if (typeof document === 'undefined') {
        return;
    }
    const slugs = collectActiveCategorySlugsFromUi();
    if (!slugs.size) {
        return;
    }

    const toggleBtn = document.querySelector('.side-panel__toggle-results');
    if ($side_panel && toggleBtn) {
        $side_panel.classList.add('side-panel--fully-opened');
        toggleBtn.classList.add('side-panel__toggle-results--active');
    }
    slugs.forEach(function (slug) {
        const s = String(slug);
        const sel =
            typeof CSS !== 'undefined' && CSS.escape
                ? '[data-categ="' + CSS.escape(s) + '"]'
                : '[data-categ="' + s.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]';
        let node;
        try {
            node = document.querySelector(sel);
        } catch (e) {
            return;
        }
        if (!node) {
            return;
        }
        let wrap = node.closest('.side-panel__categ');
        while (wrap) {
            wrap.classList.add('side-panel__categ--is-open');
            const t = wrap.querySelector(':scope > .side-panel__categ-toggle');
            if (t) {
                t.setAttribute('aria-expanded', 'true');
            }
            const parent = wrap.parentElement;
            if (!parent) {
                break;
            }
            wrap = parent.closest('.side-panel__categ');
        }
    });
}

/**
 * Remove category-related query keys before replacing with current filters.
 */
export function stripCategoryParamsFromSearchParams(searchParams) {
    const keys = [];
    searchParams.forEach(function (_, k) {
        if (k === 'categorie' || k === 'act_carte_extra' || k.startsWith('act_carte_categ_')) {
            keys.push(k);
        }
    });
    for (let i = 0; i < keys.length; i++) {
        searchParams.delete(keys[i]);
    }
}

/** Open-pin query key. */
export function stripLieuQueryParams(searchParams) {
    searchParams.delete('lieu');
}

/** True when the address bar still has `?lieu=` (reload/share before DOM/history catch up). */
export function urlHasLieuQueryParam() {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        return new URLSearchParams(window.location.search).has('lieu');
    } catch (e) {
        return false;
    }
}

function encodeCategorieSlugsForParam(vals) {
    return vals.join(CATEG_SLUG_SEP);
}

/** Decode slugs from one `categorie` query value (supports legacy comma-separated). */
function decodeCategorieSlugsFromParam(raw) {
    if (raw == null || !String(raw).trim()) {
        return [];
    }
    const s = String(raw).trim();
    if (s.includes(CATEG_SLUG_SEP)) {
        return s.split(CATEG_SLUG_SEP).map(function (x) { return x.trim(); }).filter(Boolean);
    }
    return s.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
}

function appendIncomingParamsToUrl(targetUrl, incomingParams) {
    incomingParams.forEach(function (v, k) {
        if (k === 'categorie') {
            targetUrl.searchParams.append(k, v);
        } else {
            targetUrl.searchParams.set(k, v);
        }
    });
}

/**
 * Serialize side-panel / VirtualSelect category filters for the query string.
 *
 * @returns {string} query string without leading `?`, possibly empty
 */
export function getCategoryFilterQueryString() {
    const params = new URLSearchParams();
    if (categSelects && categSelects.length) {
        Array.prototype.forEach.call(categSelects, function (el) {
            const vals = normalizeMultiSelectSnapshot(el.value);
            params.append('categorie', encodeCategorieSlugsForParam(vals));
        });
    }
    const extra = getAdditionnalCategAttribute();
    if (extra) {
        params.set('act_carte_extra', extra);
    }
    return params.toString();
}

/** Strip existing category-related keys, then append current filter state (does not touch `lieu`). */
function rebuildCategoryParamsOnUrl(url) {
    stripCategoryParamsFromSearchParams(url.searchParams);
    const qs = getCategoryFilterQueryString();
    if (qs) {
        appendIncomingParamsToUrl(url, new URLSearchParams(qs));
    }
}

/**
 * Appends current VirtualSelect / extra category filters to a URL object (`lieu` untouched).
 * Caller should strip category keys first if replacing query values.
 */
export function mergeCategoryFilterSearchParamsIntoUrl(url) {
    if (!url || !url.searchParams) {
        return;
    }
    const qs = getCategoryFilterQueryString();
    if (!qs) {
        return;
    }
    appendIncomingParamsToUrl(url, new URLSearchParams(qs));
}

/**
 * Updates the address bar to reflect current category filters (replaceState).
 * Keeps `lieu=` when present; category params stay alongside it when filters are active.
 */
export function syncCategoryHistoryReplace() {
    if (typeof window === 'undefined') {
        return;
    }
    const url = new URL(window.location.href);
    rebuildCategoryParamsOnUrl(url);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/**
 * Apply category filters from the current page URL (share / refresh).
 * Call after map DOM is ready, before first redoMarkers.
 *
 * @returns {boolean} whether any select/body attribute was changed
 */
export function applyCategoryFiltersFromUrl() {
    if (typeof window === 'undefined' || !categSelects || !categSelects.length) {
        return false;
    }
    const url = new URL(window.location.href);
    let changed = false;

    let fromUrl = url.searchParams.getAll('categorie');
    /** @deprecated Old param names; remove once bookmarks migrated */
    const legacy = !fromUrl.length;
    if (legacy) {
        let idx = 0;
        while (url.searchParams.has('act_carte_categ_' + idx)) {
            fromUrl.push(url.searchParams.get('act_carte_categ_' + idx) || '');
            idx++;
        }
    }

    Array.prototype.forEach.call(categSelects, function (el, idx) {
        if (idx >= fromUrl.length) {
            return;
        }
        const raw = fromUrl[idx];
        if (raw == null || typeof el.setValue !== 'function') {
            return;
        }
        el.setValue(decodeCategorieSlugsFromParam(raw));
        changed = true;
    });

    const extra = url.searchParams.get('act_carte_extra');
    if (extra && document.body) {
        document.body.setAttribute('data-additionnal-categ', extra);
        changed = true;
    }

    return changed;
}
